import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import CalculatorBenefits from './components/CalculatorBenefits';
import PricingSection from './components/PricingSection';
import TrustSection from './components/TrustSection';

const springEasing = [0.22, 1, 0.36, 1];

const sectionVariants = {
  initial: { opacity: 0, y: 32 },
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: springEasing },
  },
};

const navVariants = {
  initial: { opacity: 0, y: -20 },
  enter: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: springEasing },
  },
};

const mobileMenuVariants = {
  closed: {
    x: '100%',
    transition: { duration: 0.35, ease: springEasing },
  },
  open: {
    x: 0,
    transition: { duration: 0.4, ease: springEasing },
  },
};

const overlayVariants = {
  closed: { opacity: 0, transition: { duration: 0.3 } },
  open: { opacity: 1, transition: { duration: 0.3 } },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [['#features', 'Tjenester'], ['#pricing', 'Priser'], ['/contact', 'Kontakt'], ['/trust-and-security', 'Sikkerhet']];

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href?.startsWith('#')) {
      const el = document.getElementById(href?.slice(1));
      if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(href);
    }
  };

  const footerLinks = {
    'Tjenester': [
      { label: 'Bomkalkulator', path: '/toll-calculator' },
      { label: 'Ruteoptimalisering', path: '/toll-calculator' },
      { label: 'Flåtestyring', path: '/dashboard' },
    ],
    'Selskap': [
      { label: 'Om oss', path: '/about-us' },
      { label: 'Kontakt', path: '/contact' },
      { label: 'Sikkerhet', path: '/trust-and-security' },
      { label: 'FAQ', path: '/faq' },
      { label: 'Priser', path: '/pricing' },
    ],
    'Konto': [
      { label: 'Logg inn', path: '/login' },
      { label: 'Registrer deg', path: '/register' },
      { label: 'Abonnement', path: '/subscription-portal' },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Veiassistent — Norges ledende bomtjeneste</title>
        <meta name="description" content="Spar penger på norske veier med Veiassistent. Beregn bomkostnader, optimaliser ruter og motta sanntids trafikkdata." />
      </Helmet>
      {/* Minimal landing nav */}
      <motion.nav
        variants={navVariants}
        initial="initial"
        animate="enter"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: '72px',
          background: 'rgba(26,29,32,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(224,228,232,0.06)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 48px',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ maxWidth: '1440px', margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="landing-nav-padding">
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate('/landing-page')}>
            <div style={{ width: '32px', height: '32px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5">
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.04em' }}>Veiassistent</span>
          </div>

          {/* Desktop Nav links */}
          <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {navLinks?.map(([href, label]) => {
              const isActive = !href?.startsWith('#') && location?.pathname === href;
              return (
                <button
                  key={href}
                  onClick={() => handleNavClick(href)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? '2px solid #FF5722' : '2px solid transparent',
                    padding: '2px 0',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '13px',
                    fontWeight: isActive ? 700 : 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: isActive ? '#FF5722' : 'var(--color-muted-foreground)',
                    cursor: 'pointer',
                    transition: 'color 150ms ease, border-color 150ms ease',
                    pointerEvents: 'auto',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-foreground)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Desktop Auth buttons */}
          <div className="hidden-mobile" style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '10px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}
            >
              Logg inn
            </button>
            <button
              onClick={() => navigate('/register')}
              style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '10px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
            >
              Registrer deg
            </button>
          </div>

          {/* Hamburger button - mobile only */}
          <button
            className="show-mobile"
            onClick={() => setMobileOpen(prev => !prev)}
            aria-label={mobileOpen ? 'Lukk meny' : 'Åpne meny'}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              pointerEvents: 'auto',
              zIndex: 110,
            }}
          >
            <div style={{ width: '24px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <motion.span
                animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: springEasing }}
                style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }}
              />
              <motion.span
                animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'block', height: '2px', width: '100%', background: 'var(--color-foreground)', borderRadius: '2px' }}
              />
              <motion.span
                animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.3, ease: springEasing }}
                style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }}
              />
            </div>
          </button>
        </div>
      </motion.nav>
      {/* Mobile menu overlay + panel */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="overlay"
              variants={overlayVariants}
              initial="closed"
              animate="open"
              exit="closed"
              onClick={() => setMobileOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 105,
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(2px)',
              }}
            />
            {/* Slide-in panel */}
            <motion.div
              key="panel"
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                width: '280px',
                zIndex: 106,
                background: 'rgba(26,29,32,0.98)',
                backdropFilter: 'blur(20px)',
                borderLeft: '1px solid rgba(224,228,232,0.08)',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '88px',
                paddingBottom: '32px',
                paddingLeft: '32px',
                paddingRight: '32px',
              }}
            >
              {/* Nav links */}
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '32px' }}>
                {navLinks?.map(([href, label], i) => {
                  const isActive = !href?.startsWith('#') && location?.pathname === href;
                  return (
                    <motion.button
                      key={href}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 + i * 0.06, duration: 0.35, ease: springEasing }}
                      onClick={() => handleNavClick(href)}
                      style={{
                        background: isActive ? 'rgba(255,87,34,0.1)' : 'none',
                        border: 'none',
                        borderLeft: isActive ? '3px solid #FF5722' : '3px solid transparent',
                        borderRadius: '0 4px 4px 0',
                        padding: '14px 16px',
                        fontFamily: 'var(--font-heading)',
                        fontSize: '14px',
                        fontWeight: isActive ? 700 : 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: isActive ? '#FF5722' : 'var(--color-muted-foreground)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'color 150ms ease, background 150ms ease',
                      }}
                    >
                      {label}
                    </motion.button>
                  );
                })}
              </nav>

              {/* Divider */}
              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', marginBottom: '28px' }} />

              {/* Auth buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <motion.button
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.32, duration: 0.35, ease: springEasing }}
                  onClick={() => { setMobileOpen(false); navigate('/login'); }}
                  style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}
                >
                  Logg inn
                </motion.button>
                <motion.button
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.38, duration: 0.35, ease: springEasing }}
                  onClick={() => { setMobileOpen(false); navigate('/register'); }}
                  style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
                >
                  Registrer deg
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Page sections with staggered reveal */}
      <main style={{ paddingTop: '72px' }}>
        <motion.div variants={sectionVariants} initial="initial" animate="enter" transition={{ delay: 0.05 }}>
          <HeroSection />
        </motion.div>
        <motion.div variants={sectionVariants} initial="initial" animate="enter" transition={{ delay: 0.15 }}>
          <FeaturesSection />
        </motion.div>
        <motion.div variants={sectionVariants} initial="initial" animate="enter" transition={{ delay: 0.22 }}>
          <CalculatorBenefits />
        </motion.div>
        <motion.div variants={sectionVariants} initial="initial" animate="enter" transition={{ delay: 0.29 }}>
          <PricingSection />
        </motion.div>
        <motion.div variants={sectionVariants} initial="initial" animate="enter" transition={{ delay: 0.36 }}>
          <TrustSection />
        </motion.div>
      </main>
      {/* Footer */}
      <motion.footer
        variants={sectionVariants}
        initial="initial"
        animate="enter"
        transition={{ delay: 0.42 }}
        className="landing-footer-padding"
        style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(224,228,232,0.06)', padding: '48px 48px 32px' }}
      >
        <div style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div className="landing-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '48px', marginBottom: '48px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div style={{ width: '28px', height: '28px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
                </div>
                <span style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 700, color: 'var(--color-foreground)' }}>Veiassistent</span>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)', lineHeight: 1.7, maxWidth: '280px' }}>Norges ledende løsning for bomkostnadsberegning og ruteoptimalisering.</p>
            </div>
            {Object.entries(footerLinks)?.map(([title, links]) => (
              <div key={title}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', marginBottom: '16px' }}>{title}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {links?.map(link => (
                    <li key={link?.label}>
                      <button
                        onClick={() => navigate(link?.path)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          fontFamily: 'var(--font-body)',
                          fontSize: '13px',
                          color: 'var(--color-muted-foreground)',
                          cursor: 'pointer',
                          transition: 'color 150ms ease',
                          textAlign: 'left',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--color-foreground)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                      >
                        {link?.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid rgba(224,228,232,0.06)', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>© 2020 Veiassistent AS. Alle rettigheter forbeholdt.</span>
            <span style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>Personvern · Vilkår · Cookies</span>
          </div>
        </div>
      </motion.footer>
    </>
  );
};

export default LandingPage;
